import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, Loader2, Eye, EyeOff, Github } from '@/components/icons';
import { signIn, signUp, signInWithGitHub } from '../../lib/auth-client';
import { useAuth } from './AuthProvider';
import { trackSignIn, trackSignUp } from '../../lib/posthog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import StackSketchLogo from '/StackSketch-FullLogo.svg';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AuthMode = 'signin' | 'signup';

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isGitHubLoading, setIsGitHubLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Auto-close once the session becomes authenticated (the comments below always
  // claimed this happened, but the effect was missing). Radix Dialog handles ESC,
  // focus-trap, scroll-lock and the backdrop for us.
  useEffect(() => {
    if (isOpen && isAuthenticated) onClose();
  }, [isOpen, isAuthenticated, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (mode === 'signin') {
        const result = await signIn.email({
          email,
          password,
        });
        if (result.error) {
          setError(result.error.message || 'Failed to sign in');
        } else {
          trackSignIn('email');
          resetForm();
          // Modal will close via useEffect when isAuthenticated becomes true
        }
      } else {
        const result = await signUp.email({
          email,
          password,
          name,
        });
        if (result.error) {
          setError(result.error.message || 'Failed to sign up');
        } else {
          trackSignUp('email');
          resetForm();
          // Modal will close via useEffect when isAuthenticated becomes true
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setName('');
    setError(null);
  };

  const switchMode = () => {
    setMode(mode === 'signin' ? 'signup' : 'signin');
    setError(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md gap-0 overflow-hidden p-0">
        {/* Logo */}
        <div className="flex justify-center pt-6 pb-2">
          <img src={StackSketchLogo} alt="Stack Sketch" className="w-24 h-auto" />
        </div>

        {/* Header */}
        <DialogHeader className="px-6 pb-4 border-b border-border text-left">
          <DialogTitle className="text-lg font-bold text-foreground">
            {mode === 'signin' ? 'Welcome Back' : 'Create Account'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {mode === 'signin'
              ? 'Sign in to save and sync your diagrams'
              : 'Sign up to start creating diagrams'}
          </DialogDescription>
        </DialogHeader>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-6 space-y-4">
                  <AnimatePresence mode="wait">
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                      >
                        <Alert variant="destructive">
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence mode="wait">
                    {mode === 'signup' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                    <div className="space-y-2">
                          <Label htmlFor="name">Name</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              id="name"
                              type="text"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              placeholder="Your name"
                              required
                              className="pl-10"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={8}
                        className="pl-10 pr-12"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    {mode === 'signin' && (
                      <div className="mt-2 text-right">
                        <Link
                          to="/forgot-password"
                          onClick={onClose}
                          className="text-sm text-primary hover:text-primary/80 font-medium"
                        >
                          Forgot Password?
                        </Link>
                      </div>
                    )}
                  </div>

              <Button
                    type="submit"
                    disabled={isLoading || isGitHubLoading}
                    className="w-full"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        {mode === 'signin' ? 'Signing in...' : 'Creating account...'}
                      </>
                    ) : mode === 'signin' ? (
                      'Sign In'
                    ) : (
                      'Create Account'
                    )}
                  </Button>

                  <div className="relative my-4">
                    <div className="absolute inset-0 flex items-center">
                      <Separator className="w-full" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">
                        Or continue with
                      </span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={async () => {
                      setIsGitHubLoading(true);
                      setError(null);
                      try {
                        await signInWithGitHub();
                        trackSignIn('github');
                        trackSignUp('github');
                        // Modal will close via useEffect when isAuthenticated becomes true
                      } catch (err) {
                        setError('Failed to sign in with GitHub');
                        console.error(err);
                        setIsGitHubLoading(false);
                      }
                    }}
                    disabled={isLoading || isGitHubLoading}
                    className="w-full"
                  >
                    {isGitHubLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Connecting to GitHub...
                      </>
                    ) : (
                      <>
                        <Github className="w-4 h-4 mr-2" />
                        Continue with GitHub
                      </>
                    )}
                  </Button>
          </div>

          {/* Footer */}
          <div className="flex border-t border-border p-6">
            <p className="text-sm text-muted-foreground mx-auto">
              {mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
              <Button
                type="button"
                variant="link"
                onClick={switchMode}
                className="p-0 h-auto font-semibold"
              >
                {mode === 'signin' ? 'Sign up' : 'Sign in'}
              </Button>
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
